import { createContext, useContext, useState } from 'react';

import {
  Box,
  Card,
  Link,
  Stack,
  Typography,
  Button as MuiButton,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import EditAction from '@/karehero-app/actions/EditAction';
import ActionSheet from '@/designs-dev/device/ActionSheet';
import ModalScreen from '@/designs-dev/device/ModalScreen';
import UploadDocumentButton from '@/karehero-app/shell/UploadDocumentButton';
import CallSummary from '@/karehero-app/care/CallSummary';
import { type CallItem } from '@/karehero-app/care/fixtures';

/**
 * Who each action is about, by name, for a list that covers everyone you care
 * for. Set, the card leads with the person and leaves who set it to the
 * detail, and the detail names the person too. The Care hub concept sets it;
 * the master leaves it off.
 */
export const ActionPerson = createContext<
  ((action: ActionData) => string) | undefined
>(undefined);

export type ActionData = {
  id: number;
  title: string;
  regarding?: string;
  createdBy: string;
  assignedTo?: string;
  due?: string;
  repeats?: string;
  note?: string; // CAM actions only
  linkedCall?: { when: string; withName: string; from: string };
  files: string[];
  kind: 'cam' | 'user';
  done?: boolean;
  doneDate?: string;
};

// How an action is attributed to the member. CAM-set actions read as "your care
// advisor" rather than the internal CAM name; self-created keep their name.
export const actionAuthor = (action: Pick<ActionData, 'kind' | 'createdBy'>) =>
  action.kind === 'cam' ? 'your care advisor' : action.createdBy;

type EditMode = null | 'choice' | 'single' | 'series';

const ActionDetail = ({
  action,
  onClose,
  onSetDone,
  onDelete,
  resolveLinkedCall,
  initialEdit = false,
  initialEditSeries = false,
  initialDelete = false,
}: {
  action: ActionData;
  onClose: () => void;
  onSetDone: (id: number, done: boolean) => void;
  // Deleting closes the detail; only offered on self-created actions.
  onDelete?: (id: number) => void;
  /** Jump-states for handover minis: open with the edit / delete sheet up.
      initialEdit behaves like tapping Edit (one-offs open the form, series
      members open the chooser); initialEditSeries opens the series form. */
  initialEdit?: boolean;
  initialEditSeries?: boolean;
  initialDelete?: boolean;
  // When provided, pressing the linked call card opens the call summary page
  // as a full-screen modal over this sheet (the flow stays contained — no
  // tab jumps).
  resolveLinkedCall?: (
    linked: NonNullable<ActionData['linkedCall']>,
  ) => CallItem | null;
}) => {
  const [done, setDone] = useState(Boolean(action.done));
  const doneDate = action.doneDate ?? '16th May';

  // Only self-created actions can be edited or deleted. A CAM-set action is
  // read-only for the member — gated in one place so the footer AND the
  // jump-state entry points (handover minis / stories) both respect it.
  const canModify = action.kind === 'user';

  // The call summary stacked over this sheet, with a local copy of its linked
  // actions so the checkboxes work (prototype-local state, no shared store).
  const [viewingCall, setViewingCall] = useState<CallItem | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(
    !canModify
      ? null
      : initialEditSeries
        ? 'series'
        : initialEdit
          ? action.repeats
            ? 'choice'
            : 'single'
          : null,
  );
  const [confirmingDelete, setConfirmingDelete] = useState(
    canModify && initialDelete,
  );
  const openLinkedCall = () => {
    if (!action.linkedCall || !resolveLinkedCall) return;
    setViewingCall(resolveLinkedCall(action.linkedCall));
  };
  const toggleViewedAction = (id: number) =>
    setViewingCall((prev) =>
      prev
        ? {
            ...prev,
            linkedActions: prev.linkedActions.map((a) =>
              a.id === id ? { ...a, done: !a.done } : a,
            ),
          }
        : prev,
    );

  const setState = (value: boolean) => {
    setDone(value);
    onSetDone(action.id, value);
  };

  const person = useContext(ActionPerson);
  const meta = [
    person ? person(action) : action.regarding,
    `Created by ${actionAuthor(action)}`,
    action.assignedTo,
    action.due,
    action.repeats,
  ].filter(Boolean) as string[];

  const footer = done ? (
    <Box
      sx={{
        bgcolor: 'success.main',
        borderRadius: `${radius.md}px`,
        px: 2,
        py: 1.5,
      }}
    >
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        <Box sx={{ color: 'common.white', display: 'flex' }}>
          <Icon icon='check-circle' fontSize={20} />
        </Box>
        <Typography variant='body1' color='common.white'>
          Marked as done on {doneDate}.{' '}
          <Link
            component='button'
            type='button'
            onClick={() => setState(false)}
            sx={{ color: 'common.white', textDecorationColor: 'currentColor' }}
          >
            Mark as todo?
          </Link>
        </Typography>
      </Stack>
    </Box>
  ) : (
    <Stack spacing={1.5}>
      {/* only the creator can edit or delete — CAM-set actions are
          read-only for the member */}
      {canModify && (
        <Stack direction='row' spacing={1.5}>
          <MuiButton
            fullWidth
            size='large'
            variant='outlined'
            sx={{ color: 'custom.coral.600', borderColor: 'custom.coral.600' }}
            onClick={() => setConfirmingDelete(true)}
          >
            Delete
          </MuiButton>
          <MuiButton
            fullWidth
            size='large'
            variant='outlined'
            color='primary'
            // A series member asks what you're editing; a one-off goes
            // straight to the form
            onClick={() => setEditMode(action.repeats ? 'choice' : 'single')}
          >
            Edit
          </MuiButton>
        </Stack>
      )}
      <Button
        variant='primary'
        size='lg'
        isFullWidth
        ariaLabel='Mark as done'
        onPress={() => setState(true)}
      >
        Mark as done
      </Button>
    </Stack>
  );

  return (
    <>
      <BottomSheet title={action.title} onClose={onClose} footer={footer}>
        <Stack sx={{ pb: 2 }} spacing={2}>
          {/* meta */}
          <Stack spacing={1}>
            {meta.map((m, i) => (
              <Typography key={i} variant='body1' color='text.secondary'>
                {m}
              </Typography>
            ))}
          </Stack>

          {/* CAM note */}
          {action.note && (
            <Card sx={{ p: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant='subtitle2'>
                  Note from {actionAuthor(action)}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  {action.note}
                </Typography>
              </Stack>
            </Card>
          )}

          {/* linked call */}
          {action.linkedCall && (
            <Card variant='listItem' sx={{ p: 2 }} onClick={openLinkedCall}>
              <Stack
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                direction='row'
                spacing={1}
              >
                <Stack sx={{ minWidth: 0 }} spacing={0.25}>
                  <Typography variant='subtitle2'>Linked call</Typography>
                  <Typography variant='body1' color='text.secondary'>
                    {action.linkedCall.when} · with {action.linkedCall.withName}
                  </Typography>
                  <Typography variant='body1' color='text.secondary'>
                    From {action.linkedCall.from}
                  </Typography>
                </Stack>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                  <Icon icon='chevron-right' fontSize={16} />
                </Box>
              </Stack>
            </Card>
          )}

          {/* uploads */}
          <Box>
            <Typography
              variant='overline'
              color='text.secondary'
              sx={{ display: 'block', mb: 1 }}
            >
              UPLOADS
            </Typography>
            <Stack spacing={1.5}>
              <UploadDocumentButton />
              {action.files.map((f, i) => (
                <Card key={i} sx={{ p: 2 }}>
                  <Stack
                    sx={{ alignItems: 'center' }}
                    direction='row'
                    spacing={1.5}
                  >
                    <Box sx={{ color: 'primary.main', display: 'flex' }}>
                      <Icon icon='file-lines' fontSize={20} />
                    </Box>
                    <Typography
                      sx={{ flex: 1, minWidth: 0 }}
                      variant='body1'
                      noWrap
                    >
                      {f}
                    </Typography>
                    <Box sx={{ color: 'custom.neutral.500', display: 'flex' }}>
                      <Icon icon={'ellipsis-vertical' as never} fontSize={18} />
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
        </Stack>
      </BottomSheet>

      {/* Linked call: the call summary page, presented as a full-screen
          modal over this sheet — the flow stays contained, no tab jumps. */}
      {viewingCall && (
        <ModalScreen onClose={() => setViewingCall(null)}>
          {(close) => (
            <CallSummary
              call={viewingCall}
              onBack={close}
              onToggleAction={toggleViewedAction}
              onOpenAction={() => {}}
            />
          )}
        </ModalScreen>
      )}
      {(editMode === 'single' || editMode === 'series') && (
        <EditAction
          action={action}
          series={editMode === 'series'}
          onClose={() => setEditMode(null)}
        />
      )}

      {editMode === 'choice' && (
        <ActionSheet onClose={() => setEditMode(null)}>
          <MuiButton
            fullWidth
            size='large'
            variant='contained'
            color='primary'
            onClick={() => setEditMode('single')}
          >
            Edit action
          </MuiButton>
          <MuiButton
            fullWidth
            size='large'
            variant='outlined'
            color='primary'
            onClick={() => setEditMode('series')}
          >
            Edit action series
          </MuiButton>
          <MuiButton
            fullWidth
            size='large'
            variant='outlined'
            color='primary'
            onClick={() => setEditMode(null)}
          >
            Cancel
          </MuiButton>
        </ActionSheet>
      )}

      {confirmingDelete && (
        <ActionSheet onClose={() => setConfirmingDelete(false)}>
          <MuiButton
            fullWidth
            size='large'
            variant='contained'
            color='error'
            onClick={() => {
              setConfirmingDelete(false);
              onDelete?.(action.id);
              onClose();
            }}
          >
            Delete action
          </MuiButton>
          {action.repeats && (
            <MuiButton
              fullWidth
              size='large'
              variant='outlined'
              color='error'
              onClick={() => {
                setConfirmingDelete(false);
                onDelete?.(action.id);
                onClose();
              }}
            >
              Delete all repeating actions
            </MuiButton>
          )}
          <MuiButton
            fullWidth
            size='large'
            variant='outlined'
            color='primary'
            onClick={() => setConfirmingDelete(false)}
          >
            Cancel
          </MuiButton>
        </ActionSheet>
      )}
    </>
  );
};

export default ActionDetail;
